import React, { useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, Alert, TextField, Button, Stack, TableSortLabel, IconButton
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { Outlet, Link, useLocation } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";

export default function UserBirdiesPage() {
  const auth = useAuth();
  const token = auth.user?.access_token || auth.user?.id_token;
  const [birdies, setBirdies] = useState([]);
  const [error, setError] = useState("");
  const [userIdInput, setUserIdInput] = useState("");
  const [currentUserId, setCurrentUserId] = useState("");
  const [order, setOrder] = useState("desc"); // "asc" or "desc"
  const isMyBirdies = !currentUserId || currentUserId === auth.user?.profile?.sub;
  const [loading, setLoading] = useState(false);
  const location = useLocation();

  // Fetch birdies for a given user_id (or self if blank)
  const fetchBirdies = (userId = "") => {
    let url = "https://elk87mfiog.execute-api.us-east-1.amazonaws.com/my-birdies";
    if (userId) {
      url += `?user_id=${encodeURIComponent(userId)}`;
    }
    setLoading(true);
    fetch(url, {
      headers: {
        "Authorization": `Bearer ${token}`,
        "Content-Type": "application/json"
      }
    })
      .then(res => res.json())
      .then(data => {
        if (data.message) {
          setBirdies([]);
          setError(data.message);
        } else {
          setBirdies(data);
          setCurrentUserId(userId);
          setError("");
        }
      })
      .catch(() => setError("Failed to load birdies."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (token) fetchBirdies();
    // eslint-disable-next-line
  }, [token]);

  const handleUserIdSubmit = (e) => {
    e.preventDefault();
    fetchBirdies(userIdInput);
  };

  // Sort birdies by date
  const sortedBirdies = [...birdies].sort((a, b) => {
    if (!a.date || !b.date) return 0;
    if (order === "asc") {
      return a.date.localeCompare(b.date);
    } else {
      return b.date.localeCompare(a.date);
    }
  });

  const handleSortToggle = () => {
    setOrder(order === "asc" ? "desc" : "asc");
  };

  // Delete handler
  const handleDelete = async (birdie) => {
    if (!window.confirm("Are you sure you want to delete this birdie?")) return;
    setLoading(true);
    try {
      const payload = {
        user_id: birdie.user_id,
        timestamp: birdie.timestamp,
      };
      console.log("Deleting birdie:", payload);
      const res = await fetch("https://elk87mfiog.execute-api.us-east-1.amazonaws.com/birdie", {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (res.ok) {
        fetchBirdies(currentUserId);
      } else {
        setError(data.message || "Failed to delete birdie.");
      }
    } catch (err) {
      setError("Error: " + err.message);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <>
      <img
          src="/logo192.png"
          alt="Logo192"
          width={100}
          height={100}
          style={{ display: "block", margin: "0 auto" }}
      />
    <Box sx={{ mt: 2, textAlign: "center" }}>
      <Button
        component={Link}
        to="input"
        variant="contained"
        color="secondary"
        sx={{ mb: 2 }}
      >
        Input Birdie
      </Button>
    </Box>
    {location.pathname !== "/my-birdies/input" && (
      <TableContainer component={Paper} sx={{ mt: 4, maxWidth: 700, mx: "auto", borderRadius: 3, boxShadow: 3 }}>
        <Typography variant="h5" align="center" sx={{ mt: 2, mb: 2 }}>
          {currentUserId ? `Birdies for ${currentUserId}` : "My Birdies"}
        </Typography>
        <form onSubmit={handleUserIdSubmit}>
          <Stack direction="row" spacing={2} sx={{ mb: 2, justifyContent: "center" }}>
            <TextField
              label="User ID"
              value={userIdInput}
              onChange={e => setUserIdInput(e.target.value)}
              size="small"
              placeholder="Enter user ID"
            />
            <Button type="submit" variant="contained" color="secondary">
              View Birdies
            </Button>
          </Stack>
        </form>
        {error && <Alert severity="error">{error}</Alert>}
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={true}
                  direction={order}
                  onClick={handleSortToggle}
                >
                  Date
                </TableSortLabel>
              </TableCell>
              <TableCell>Course</TableCell>
              <TableCell>Hole</TableCell>
              <TableCell>Eagle</TableCell>
              <TableCell>Notes</TableCell>
              {isMyBirdies && <TableCell>Delete</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedBirdies.map((row, idx) => (
              <TableRow key={row.timestamp || idx}>
                <TableCell>{row.date}</TableCell>
                <TableCell>{row.course}</TableCell>
                <TableCell>{row.hole}</TableCell>
                <TableCell>{row.eagle ? "🦅" : ""}</TableCell>
                <TableCell>{row.notes}</TableCell>
                {isMyBirdies && (
                  <TableCell>
                    <IconButton color="error" onClick={() => handleDelete(row)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    )}
    <Outlet />
    </>  
  );
}